import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChatBubbleLeftRightIcon, ChevronDownIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import { smsAPI, type SmsTemplate, authAPI, extractErrorMessage } from '../services/api';
import { userAssistantConfigService, type UserAssistantConfig } from '../services/userAssistantConfigService';

export type SmsLeadTarget = {
  id: string;
  name: string;
  phone: string;
};

type SendSMSModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lead: SmsLeadTarget | null;
  setToast: (t: { type: 'success' | 'error'; message: string } | null) => void;
};

function fillPlaceholders(body: string, lead: SmsLeadTarget | null, agentName: string, callbackNumber: string): string {
  if (!body) return body;
  let result = body;
  result = result.replace(/\{\{lead_name\}\}/g, lead?.name || '');
  result = result.replace(/\{\{agent_name\}\}/g, agentName || '');
  result = result.replace(/\{\{callback_number\}\}/g, callbackNumber || '');
  result = result.replace(/\{\{appointment_time\}\}/g, '');
  return result;
}

const SendSMSModal: React.FC<SendSMSModalProps> = ({ isOpen, onClose, lead, setToast }) => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [sending, setSending] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [agentName, setAgentName] = useState('');
  const [callbackNumber, setCallbackNumber] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId('');
    setLoading(true);
    setTemplatesError(null);
    smsAPI
      .getTemplates()
      .then((list) => setTemplates(Array.isArray(list) ? list : []))
      .catch((error: any) => {
        setTemplates([]);
        if (error?.response?.status === 403) {
          setTemplatesError('SMS is not enabled for your current plan.');
        } else {
          setTemplatesError(extractErrorMessage(error, 'Failed to load SMS templates.'));
        }
      })
      .finally(() => setLoading(false));

    Promise.all([
      userAssistantConfigService.getConfig().catch(() => null as UserAssistantConfig | null),
      authAPI.getCurrentUser().catch(() => null as any),
    ]).then(([cfg, u]) => {
      const first = (u?.first_name || '').trim();
      const last = (u?.last_name || '').trim();
      const fullName = [first, last].filter(Boolean).join(' ').trim();
      // Prefer logged-in user's first/last name for {{agent_name}}.
      setAgentName(fullName || (cfg?.agent_name || '').trim() || (u?.email || '').trim() || '');
      setCallbackNumber((u?.phone || '').trim());
    }).catch(() => {});
  }, [isOpen]);

  const selectedTemplate = templates.find((t) => t.id === selectedId);

  const prevSelectedIdRef = useRef('');
  useEffect(() => {
    if (selectedId && selectedId !== prevSelectedIdRef.current) {
      const tpl = templates.find((t) => t.id === selectedId);
      if (tpl) setEditedBody(fillPlaceholders(tpl.body, lead, agentName, callbackNumber));
      setIsEditing(false);
    }
    if (!selectedId) { setEditedBody(''); setIsEditing(false); }
    prevSelectedIdRef.current = selectedId;
  }, [selectedId, templates, lead, agentName, callbackNumber]);

  const handleSend = async () => {
    if (!lead || !selectedId || !editedBody.trim()) return;
    setSending(true);
    try {
      const result = await smsAPI.sendSms({ lead_id: lead.id, template_id: selectedId, custom_body: editedBody.trim() });
      setToast({ type: 'success', message: result.message || 'SMS sent successfully' });
      onClose();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || 'Failed to send SMS';
      setToast({ type: 'error', message: detail });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-3xl rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Send SMS</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {lead && (
            <p className="text-sm text-gray-300">
              Send SMS to <span className="font-medium text-white">{lead.name}</span>
              {lead.phone && <span className="text-gray-400"> ({lead.phone})</span>}
            </p>
          )}

          <div ref={dropdownRef} className="relative">
            <label id="sms-template-label" className="block text-sm font-medium text-gray-300 mb-1.5">
              Select template *
            </label>
            {loading ? (
              <p className="text-gray-400 text-sm">Loading templates...</p>
            ) : templatesError ? (
              <p className="text-red-300 text-sm">{templatesError}</p>
            ) : templates.length === 0 ? (
              <p className="text-gray-400 text-sm">
                No SMS templates yet.{' '}
                <a href="/sms-templates" className="text-cyan-400 hover:underline">
                  Create one
                </a>{' '}
                first.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                  aria-labelledby="sms-template-label"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-left text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {selectedId
                      ? templates.find((t) => t.id === selectedId)?.name ?? 'Choose a template'
                      : 'Choose a template'}
                  </span>
                  <ChevronDownIcon
                    className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {dropdownOpen && (
                  <ul
                    role="listbox"
                    aria-labelledby="sms-template-label"
                    className="absolute left-0 right-0 top-full mt-1 z-20 py-1 rounded-lg bg-slate-800 border border-slate-600/50 shadow-xl max-h-48 overflow-y-auto custom-scrollbar"
                  >
                    <li
                      role="option"
                      aria-selected={!selectedId}
                      onClick={() => { setSelectedId(''); setDropdownOpen(false); }}
                      className={`px-3 py-2.5 cursor-pointer text-sm truncate ${!selectedId ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                    >
                      Choose a template
                    </li>
                    {templates.map((t) => (
                      <li
                        key={t.id}
                        role="option"
                        aria-selected={selectedId === t.id}
                        onClick={() => { setSelectedId(t.id); setDropdownOpen(false); }}
                        className={`px-3 py-2.5 cursor-pointer text-sm truncate ${selectedId === t.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                      >
                        {t.name}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {selectedTemplate && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-3">
                <p className="text-xs font-medium text-gray-400 mb-1">Template</p>
                <p className="text-sm text-gray-400 whitespace-pre-wrap">{selectedTemplate.body}</p>
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-900/10 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-emerald-400">Message to send</p>
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors text-xs font-medium"
                      title="Save changes"
                    >
                      <CheckIcon className="w-3.5 h-3.5" />
                      <span>Done</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(true);
                        setTimeout(() => textareaRef.current?.focus(), 50);
                      }}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-gray-400 hover:text-emerald-400 hover:bg-white/10 transition-colors text-xs"
                      title="Edit message"
                    >
                      <PencilIcon className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <>
                    <textarea
                      ref={textareaRef}
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      rows={6}
                      className="w-full px-2.5 py-2 bg-white/5 border border-emerald-500/30 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
                    />
                    <p className="text-xs text-gray-400 mt-1">{editedBody.length} characters</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">{editedBody}</p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || loading || !selectedId || !editedBody.trim() || templates.length === 0 || !lead}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending...' : 'Send SMS'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendSMSModal;
